import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import './InputEntries.scss'

interface InputEntriesProps {
  value?: Record<string, string>
  onChange?: (value: Record<string, string>) => void
  disabled?: boolean
  title?: string
  hint?: string
  error?: string
}

export const InputEntries: React.FC<InputEntriesProps> = ({ value = {}, onChange, disabled = false, title, hint, error }) => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<[string, string][]>([])
  const isUserInputRef = useRef(false)

  const handleInputChange = useCallback(() => {
    const newValue = Object.fromEntries(entries.filter(([key]) => key?.length))
    onChange?.(newValue)
  }, [entries, onChange])

  // Memoize entries to prevent unnecessary re-renders
  const memoizedEntries = useMemo(() => entries, [entries])

  // Only update entries when value prop changes from outside (not from user input)
  useEffect(() => {
    if (isUserInputRef.current) {
      isUserInputRef.current = false
      return
    }

    const valueEntries = Object.entries(value)
    setEntries(valueEntries)
  }, [value])

  // Remove the debounced useEffect that was causing re-renders

  const addEntry = () => {
    const newEntries: [string, string][] = [...entries, ['', '']]
    setEntries(newEntries)
    // Don't call handleInputChange here as the new entry is empty
  }

  const removeEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index)
    setEntries(newEntries)
    // Call handleInputChange immediately when removing entry
    setTimeout(() => {
      const newValue = Object.fromEntries(newEntries.filter(([key]) => key?.length))
      onChange?.(newValue)
    }, 0)
  }

  const updateEntry = (index: number, key: string, value: string) => {
    isUserInputRef.current = true
    const newEntries = [...entries]
    newEntries[index] = [key, value]
    setEntries(newEntries)
    // Don't call handleInputChange immediately to avoid re-rendering
    // It will be called on onBlur
  }

  return (
    <div className={`mb-3 ${error ? 'pb-3' : ''}`}>
      <div className="row">
        <div className="d-flex align-items-center mb-2">
          {title && (
            <label className="form-label mb-0" htmlFor="inputId">
              {title}
            </label>
          )}
          <Button type="button" variant="outline-primary" size="sm" className="ms-4" onClick={addEntry} disabled={disabled}>
            <i className="bi bi-plus-circle"></i>&nbsp;{t('add')}
          </Button>
        </div>
        <div className="position-relative">
          {memoizedEntries.map((entry, index) => (
            <div key={`entry-${index}`} className="input-group mb-3">
              <Form.Control
                type="text"
                value={entry[0]}
                onChange={(e) => updateEntry(index, e.target.value, entry[1])}
                onBlur={handleInputChange}
                disabled={disabled}
                autoComplete="off"
                placeholder={t('key')}
              />
              <Form.Control
                type="text"
                value={entry[1]}
                onChange={(e) => updateEntry(index, entry[0], e.target.value)}
                onBlur={handleInputChange}
                disabled={disabled}
                autoComplete="off"
                placeholder={t('value')}
              />
              <Button type="button" variant="outline-secondary" onClick={() => removeEntry(index)} disabled={disabled}>
                <i className="bi bi-trash text-danger"></i>
              </Button>
            </div>
          ))}
          {hint && <small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: hint }}></small>}
          {error && <div className="invalid-feedback position-absolute top-100">{error}</div>}
        </div>
      </div>
    </div>
  )
}
